import { useEffect, useRef, useState } from "react";
import { useNavigationGuard } from "../NavigationGuardContext";

/**
 * Registers a navigation guard whenever `dirty` is true.
 * Tab clicks in ClientEditLayout will trigger the guard promise before navigating.
 * Also wires the browser beforeunload warning.
 *
 * Returns { showModal, resolveGuard } so the page can render a "Save / Discard / Cancel" modal.
 * Call resolveGuard(true) to let navigation proceed, resolveGuard(false) to cancel it.
 */
export function useNavGuard(dirty) {
  const guardRef = useNavigationGuard();
  const [showModal, setShowModal] = useState(false);
  const resolveRef = useRef(null);

  useEffect(() => {
    if (!dirty) {
      guardRef.current = null;
      return;
    }
    guardRef.current = () =>
      new Promise((resolve) => {
        resolveRef.current = resolve;
        setShowModal(true);
      });
    return () => {
      guardRef.current = null;
    };
  }, [dirty, guardRef]);

  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  function resolveGuard(value) {
    setShowModal(false);
    resolveRef.current?.(value);
    resolveRef.current = null;
  }

  return { showModal, resolveGuard };
}
